/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { forwardRef } from 'react';
import { CustomContentProps, useSnackbar } from 'notistack';
import { Alert, AlertColor } from '@mui/material';
import { FormattedMessage } from 'react-intl';

/**
 * Custom snackbar component that renders Material-UI Alert components.
 * This component is used with notistack v3 to provide consistent toast styling.
 *
 * @param id - Unique identifier for the snackbar
 * @param message - Message to display (translation key)
 * @param variant - Alert severity (success, error, warning, info)
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
const CustomSnackbar = forwardRef<HTMLDivElement, CustomContentProps>((props, ref) => {
	// eslint-disable-next-line react/prop-types
	const { id, message, variant } = props;
	const { closeSnackbar } = useSnackbar();

	const handleDismiss = () => {
		closeSnackbar(id);
	};

	// Map notistack variants to Material-UI Alert severities
	const severity: AlertColor = (variant === 'default' ? 'info' : variant) as AlertColor;

	return (
		<Alert ref={ref} onClose={handleDismiss} severity={severity}>
			<FormattedMessage id={message as string} />
		</Alert>
	);
});

CustomSnackbar.displayName = 'CustomSnackbar';

export default CustomSnackbar;
